import { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import media from "styled-media-query";
import { useExperiment } from "@growthbook/growthbook-react";
import {
	Track,
	UserScheme,
	CoorporateScheme,
	Constant,
	LetterScheme,
	ProductScheme,
	Volunteer,
} from "client-v2";
import WizardContainer, {
	WizardContainerRef,
} from "../../components/container/WizardContainer";
import Personal from "./section/Personal";
import Search from "./section/Search";
import Stamp from "./section/Stamp";
import Letter from "../../components/section/letter/Letter";
import LetterRight from "./section/LetterRight";
import EditLetter from "../../components/section/letter/EditLetter";
import PDFLetter from "../../components/section/letter/PDFLetter";
import Price from "../../components/section/donation/Price";
import PaymentMethod from "../../components/section/donation/PaymentMethod";
import { ChevronDown, Sparkles } from "lucide-react";
import { Feedback, Text } from "@adoptaunabuelo/react-components";
import { useDispatch, useSelector } from "react-redux";
import { actionsCreators, State } from "../../redux";
import { bindActionCreators } from "redux";
import Success from "./section/Success";
import SuccessRight from "../../components/section/success/SuccessCrossSell";
import PersonalRight from "./section/PersonalRight";
import CurrencySelector from "../../components/selector/CurrencySelector";
import PriceRight from "./section/PriceRight";
import CheckoutCorporate from "./CheckoutCorporate";
import PackCell from "../../components/cell/PackCell";

const AbsoluteDiv = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	color: white;
	background: linear-gradient(72deg, #5963f6 0%, #cd59f6 100%);
	background-clip: text;
	-webkit-background-clip: text;
	-webkit-text-fill-color: transparent;
	font-family: "Poppins";
	font-size: 14px;
	cursor: pointer;
	gap: 4px;
	:hover {
		text-decoration: underline;
	}
`;
const CorporateLogo = styled.img`
	height: 48px;
	width: auto;
`;
const ArrowAnimationView = styled.div`
	display: none;
	position: fixed;
	bottom: 0px;
	right: 0px;
	left: 0px;
	width: 100%;
	padding: 52px 0px 16px;
	background: linear-gradient(0deg, #fff 0%, rgba(255, 255, 255, 0) 100%);
	align-items: flex-end;
	justify-content: center;
	${media.lessThan("medium")`
		display: flex;
    `}
`;

const Checkout = (props: Props) => {
	const priceExperiment = useExperiment({
		key: "price_step_variation",
		variations: [false, true],
	});
	const wizard = useRef<WizardContainerRef>(null);
	const tips = Constant.letterTips();
	const dispatch = useDispatch();
	const { setShowError, setCurrentUser } = bindActionCreators(
		actionsCreators,
		dispatch
	);
	const isMobile: boolean = useSelector(
		(state: State) => state.screen.isMobile
	);
	const translation = useSelector(
		(state: State) => state.location.translation
	);
	const config = useSelector((state: State) => state.location.config);
	const appLocation = useSelector((state: State) => state.location.location);
	const currentUser = useSelector((state: State) => state.user.currentUser);
	const view = "letter";

	const [loading, setLoading] = useState<boolean>(true);
	const [grandpa, setGrandpa] = useState<UserScheme | undefined>(undefined);
	const [letter, setLetter] = useState<{
		object?: LetterScheme;
		content: string;
		font: string;
		personalInfo: boolean;
		stamp: boolean;
		image?: string;
	}>({
		content: "",
		font: "Poppins",
		personalInfo: true,
		stamp: true,
	});
	const [product, setProduct] = useState<ProductScheme | undefined>(
		undefined
	);
	const [didSubscribe, setDidSubscribe] = useState(false);
	const [tip, setTip] = useState<string | undefined>(undefined);
	const [showCorporate, setShowCorporate] = useState(
		props.corporate ? true : false
	);

	useEffect(() => {
		//Track to Mixpanel
		Track.event({
			event: "letter view init",
			user: currentUser,
			params: {
				view: view,
			},
		});

		//init
		if (currentUser) {
			if (props.prevLetter) {
				setLetter({
					content: props.prevLetter.content,
					font: "Poppins",
					personalInfo: true,
					stamp: true,
				});
				setGrandpa(props.prevLetter.grandpa);
			}
		}
		setLoading(false);
	}, []);

	const renderTipButton = () => (
		<AbsoluteDiv
			onClick={() => {
				const randomIndex = Math.floor(Math.random() * tips.length);
				const randomValue = tips[randomIndex];
				setTip(
					appLocation?.language === "en"
						? randomValue.title_en
						: randomValue.title
				);
			}}
		>
			<Sparkles color={"#6C62F6"} height={20} width={20} />
			{translation.component_input_letter_inspiration}
		</AbsoluteDiv>
	);

	return loading ? null : showCorporate ? (
		<CheckoutCorporate
			{...props}
			onFinish={() => setShowCorporate(false)}
		/>
	) : (
		<WizardContainer
			ref={wizard}
			corporate={props.corporate}
			personalView={
				//Personal data
				{
					birthdayRequired: true,
					navigationRightView: props.corporate?.logo && (
						<CorporateLogo src={props.corporate.logo.url} />
					),
					leftView: currentUser ? (
						<Personal
							user={currentUser}
							corporate={props.corporate}
							onFinish={(result) => {
								if (result.error) {
									setShowError({
										show: true,
										message: result.error,
									});
								} else if (result.data) {
									setCurrentUser(result.data);
									wizard.current?.goNext();
								}
							}}
						/>
					) : null,
					rightViewStyle: { flex: 1 },
					rightView: !isMobile ? <PersonalRight /> : null,
				}
			}
			views={[
				//Search grandpa
				{
					navigationRightView: props.corporate?.logo && (
						<CorporateLogo src={props.corporate.logo.url} />
					),
					hideNavBar: true,
					leftView: !isMobile ? (
						<Stamp
							position="left"
							hobbies={grandpa?.data?.hobbies}
						/>
					) : null,
					centralContainerStyle: {
						backgroundColor: "transparent",
						overflow: "hidden",
						paddingTop: 0,
						maxHeight: "100%",
					},
					centralView: currentUser ? (
						<Search
							user={currentUser}
							corporate={props.corporate}
							prevGrandpa={grandpa}
							onGrandpaSearch={(result) => {
								if (result.error) {
									setShowError({
										show: true,
										message: result.error,
									});
								} else if (result.data) {
									setGrandpa(result.data);
									setLetter({
										...letter,
										content: "",
									});
								} else {
									setGrandpa(undefined);
								}
							}}
							onFinish={(result) => {
								if (result.error) {
									setShowError({
										show: true,
										message: result.error,
									});
								} else if (result.data) {
									wizard.current?.goNext();
								}
							}}
						/>
					) : null,
					rightView: !isMobile ? (
						<Stamp
							position="right"
							hobbies={grandpa?.data?.hobbies}
						/>
					) : null,
					hideBackButton: true,
				},
				//Write letter
				{
					navigationRightView: isMobile
						? renderTipButton()
						: props.corporate?.logo && (
								<CorporateLogo src={props.corporate.logo.url} />
						  ),
					centralContainerStyle: {
						padding: 0,
						maxHeight: "unset",
						borderRadius: isMobile ? 0 : 24,
					},
					centralViewStyle: { width: "100%" },
					centralView:
						currentUser && grandpa ? (
							<>
								{tip && (
									<Feedback
										isVisible={tip ? true : false}
										type={"custom"}
										style={{
											transformOrigin: "right top",
											background:
												"linear-gradient(72deg, #5963F6 0%, #CD59F6 100%)",
											borderRadius: 12,
											top: isMobile ? 0 : 12,
											right: isMobile ? 16 : 24,
											left: isMobile ? 16 : 24,
											width: isMobile
												? "unset"
												: "calc(100% - 48px)",
											maxWidth: "unset",
											bottom: "unset",
										}}
										text={tip}
										closeAfter={5000}
										showClose={true}
										onClose={() => setTip(undefined)}
									/>
								)}
								<Letter
									user={currentUser}
									grandpa={grandpa}
									corporate={props.corporate}
									defaultValue={letter.content}
									TipButton={renderTipButton()}
									onFinish={(result) => {
										if (result.error) {
											setShowError({
												show: true,
												message: result.error,
											});
										} else if (result.data) {
											setLetter({
												...letter,
												content: result.data,
											});
											wizard.current?.goNext();
										}
									}}
								/>
							</>
						) : null,
					rightViewStyle: isMobile ? { display: "none" } : {},
					rightView: grandpa ? (
						<LetterRight grandpa={grandpa} />
					) : null,
				},
				//Edit letter PDF
				{
					navigationLeftView: isMobile && (
						<Text type="b1" weight="medium">
							Haz tu carta única
						</Text>
					),
					navigationRightView: props.corporate?.logo && (
						<CorporateLogo src={props.corporate.logo.url} />
					),
					leftViewStyle: {
						display: isMobile ? "none" : "flex",
					},
					leftView:
						currentUser && grandpa ? (
							<EditLetter
								title={translation.letter_edit_title}
								subtitle={translation.letter_edit_subtitle}
								user={currentUser}
								grandpa={grandpa}
								view={view}
								corporate={props.corporate}
								{...letter}
								onChange={(data) =>
									setLetter({
										content: letter.content,
										...data,
									})
								}
								onFinish={async (result) => {
									if (result.error) {
										setShowError({
											show: true,
											message: result.error,
										});
									} else if (result.data) {
										setLetter({
											...letter,
											object: result.data,
										});
										if (props.corporate) {
											wizard.current?.goNext(3);
										} else if (
											(
												await Volunteer.isSubscriptor(
													currentUser.objectId
												)
											).data
										) {
											wizard.current?.goNext(3);
										} else wizard.current?.goNext();
									}
								}}
							/>
						) : null,
					centralContainerStyle: {
						backgroundColor: "transparent",
						paddingTop: 24,
					},
					centralViewStyle: { width: 550 },
					centralView:
						currentUser && grandpa ? (
							<PDFLetter
								user={currentUser}
								grandpa={grandpa}
								corporate={props.corporate}
								view={view}
								{...letter}
								onChange={(data) =>
									setLetter({
										content: letter.content,
										...data,
									})
								}
								onFinish={async (result) => {
									if (result.error) {
										setShowError({
											show: true,
											message: result.error,
										});
									} else if (result.data) {
										setLetter({
											...letter,
											object: result.data,
										});
										if (props.corporate) {
											wizard.current?.goNext(3);
										} else if (
											(
												await Volunteer.isSubscriptor(
													currentUser.objectId
												)
											).data
										) {
											wizard.current?.goNext(3);
										} else wizard.current?.goNext();
									}
								}}
							/>
						) : undefined,
				},
				//Select price view
				{
					navigationRightView: (
						<div
							style={{
								display: "flex",
								flex: 1,
								gap: 8,
								marginRight: 8,
								alignItems: "center",
							}}
						>
							{!isMobile && props.corporate?.logo && (
								<CorporateLogo src={props.corporate.logo.url} />
							)}
							<CurrencySelector />
						</div>
					),
					centralViewStyle: {
						width: priceExperiment.value ? "100%" : "",
					},
					centralView: (
						<Price
							title={
								"Da un paso más y ayúdanos a seguir con nuestra misión"
							}
							subtitle={
								"Estás regalando {{value}} horas de compañía a una persona mayor"
							}
							design={
								priceExperiment.value ? "secondary" : "primary"
							}
							type_to_eur={0.602}
							subtitle_type="horas"
							options={["monthly", "annually"]}
							amountSelector="selector"
							view={view}
							skipModalProps={{
								title: "Los grandes proyectos se crean en comunidad",
								duration: 30,
								video: "https://data.adoptaunabuelo.org/video/letter/skip_modal.webm",
								hideAlert: true,
							}}
							onSkip={() => wizard.current?.goNext(2)}
							onFinish={(result) => {
								if (result.error) {
									setShowError({
										show: true,
										message: result.error,
									});
								} else if (result.data) {
									setProduct(result.data);
									wizard.current?.goNext();
								}
							}}
						>
							{isMobile ? (
								<PriceRight product={product} />
							) : (
								<></>
							)}
						</Price>
					),
					rightViewStyle: {
						overflowY: "scroll",
						height: "calc(100vh - 72px)",
						scrollbarWidth: "none",
					},
					rightView: !isMobile && <PriceRight product={product} />,
				},
				//Select payment method
				{
					navigationRightView: props.corporate?.logo && (
						<CorporateLogo src={props.corporate.logo.url} />
					),
					centralView:
						product && currentUser ? (
							<PaymentMethod
								product={product}
								user={currentUser}
								corporate={props.corporate}
								view={view}
								onFinish={(result) => {
									if (result.error) {
										setShowError({
											show: true,
											message: result.error,
										});
									} else if (result.data) {
										setDidSubscribe(true);
										wizard.current?.goNext();
									}
								}}
								onProductChange={(result) => {
									if (result.error) {
										setShowError({
											show: true,
											message: result.error,
										});
									} else if (result.data) {
										setProduct(result.data);
									}
								}}
							/>
						) : null,
				},
				//Success view
				{
					containerStyle: { overflowY: "scroll" },
					navigationRightView: props.corporate?.logo && (
						<CorporateLogo src={props.corporate.logo.url} />
					),
					centralView: currentUser ? (
						<>
							<Success
								user={currentUser}
								letter={letter.object}
								grandpa={grandpa}
								didSubscribe={didSubscribe}
								corporate={props.corporate}
							>
								{isMobile ? (
									<SuccessRight
										initialCell={
											<PackCell
												key={`pack-cell`}
												title={
													"Colabora enviando una carta con el Pack Solidario de Escritura"
												}
												image={
													"https://adoptaunabuelo.org/wp-content/uploads/2025/11/pack-691c881bc9aa4.webp"
												}
												amount={
													config?.attributes
														?.PACK_INFO?.amount || 0
												}
											/>
										}
									/>
								) : null}
							</Success>
							<ArrowAnimationView>
								<ChevronDown />
							</ArrowAnimationView>
						</>
					) : null,
					rightView: !isMobile ? (
						<SuccessRight
							animationTime={8.5}
							initialCell={
								<PackCell
									key={`pack-cell`}
									title={
										"Colabora enviando una carta con el Pack Solidario de Escritura"
									}
									image={
										"https://adoptaunabuelo.org/wp-content/uploads/2025/11/pack-691c881bc9aa4.webp"
									}
									amount={
										config?.attributes?.PACK_INFO?.amount ||
										0
									}
								/>
							}
						/>
					) : null,
					hideBackButton: true,
				},
			]}
		/>
	);
};
export default Checkout;
export interface Props {
	corporate?: CoorporateScheme;
	nLetters?: number;
	prevLetter?: {
		content: string;
		grandpa: UserScheme;
	};
}
